import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { LIMITS } from '@kidzim/shared';
import { Button, Card, Input, Logo } from '../design-system/components';
import { api, type ApiError } from '../lib/api';

/**
 * Landing page for the "forgot password" link. The token from the query
 * string is the sole authorization — no current password needed, since the
 * point is the grown-up can't provide one. Public.
 */
export function ResetPassword() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const token = params.get('token') ?? '';
  const [newPassword, setNewPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await api('/api/auth/password-reset/confirm', { method: 'POST', body: JSON.stringify({ token, newPassword }) });
      setDone(true);
    } catch (err) {
      const e = err as ApiError;
      setError(e.error === 'invalid_token' ? 'This reset link is invalid or has expired. Request a new one.' : 'Could not reset your password. Try again.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="kz-center">
      <Card style={{ maxWidth: 420, width: '100%' }}>
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 'var(--space-4)' }}>
          <Logo height={36} />
        </div>
        {done ? (
          <div className="kz-stack" style={{ gap: 'var(--space-4)', textAlign: 'center' }}>
            <h2>Password updated ✓</h2>
            <p className="kz-muted">You've been signed out everywhere. Sign in with your new password.</p>
            <Button onClick={() => nav('/parent')}>Sign in</Button>
          </div>
        ) : !token ? (
          <div className="kz-stack" style={{ gap: 'var(--space-4)', textAlign: 'center' }}>
            <h2>This link didn’t work</h2>
            <p className="kz-muted">The reset link is missing its token. Request a new one from the sign-in page.</p>
            <Link to="/parent"><Button variant="secondary">Sign in</Button></Link>
          </div>
        ) : (
          <form className="kz-stack" onSubmit={submit}>
            <p className="kz-muted" style={{ font: 'var(--type-small)', marginBottom: 'var(--space-2)' }}>
              Choose a new password. This signs out every device using your account.
            </p>
            <Input
              label="New password"
              type="password"
              autoComplete="new-password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              minLength={LIMITS.MIN_PARENT_PASSWORD_LEN}
              required
            />
            {error && <p className="kz-error-text">{error}</p>}
            <Button type="submit" full disabled={busy}>{busy ? 'One sec…' : 'Reset password'}</Button>
          </form>
        )}
      </Card>
    </div>
  );
}
